import React from 'react';
import CaseActionDialog from './CaseActionDialog';
import { useTranslation } from 'react-i18next';
import { FormControl, InputLabel, Select, MenuItem, Box } from '@mui/material';

/**
 * CaseDuplicateDialog Component
 * 
 * A specialized dialog component for duplicating/copying cases.
 * This component provides a focused interface for case duplication operations.
 * 
 * Features:
 * - Dedicated interface for duplicating cases
 * - Auto-generates default copy name with " (Copy)" suffix
 * - Input validation (prevents empty names)
 * - Keyboard support (Enter to submit)
 * - Internationalization support
 * - Consistent styling with the application theme
 * - Automatic focus on input field when opened
 * - Collection selection support
 */
const CaseDuplicateDialog = ({
  open,
  onClose,
  originalTitle = '',
  onDuplicate,
  primaryColor = '#5757b0',
  hoverColor = '#7777d0',
  collections = [],
  defaultCollectionId = ''
}) => {
  const { t } = useTranslation();
  const [duplicateValue, setDuplicateValue] = React.useState('');
  const [selectedCollection, setSelectedCollection] = React.useState('');

  // Generate default copy name and set collection when dialog opens
  React.useEffect(() => {
    if (open) {
      if (originalTitle) {
        const copyText = t('sidebar.copy_suffix') || ' (Copy)';
        setDuplicateValue(originalTitle + copyText);
      }
      // Set default collection if provided, otherwise empty string (No Collection)
      setSelectedCollection(defaultCollectionId || '');
    }
  }, [open, originalTitle, defaultCollectionId, t]);

  const handleSubmit = () => {
    const trimmedName = duplicateValue.trim();
    
    if (!trimmedName) {
      // Don't submit if name is empty - the dialog will handle validation
      return;
    }

    onDuplicate(trimmedName, selectedCollection);
    handleClose();
  };

  const handleClose = () => {
    setDuplicateValue('');
    setSelectedCollection('');
    onClose();
  };

  // Custom content to render below the title input
  const renderExtraContent = () => {
    if (!collections || collections.length === 0) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <FormControl fullWidth variant="outlined" size="small">
          <InputLabel id="collection-select-label">
            {t('sidebar.collection') || 'Collection'}
          </InputLabel>
          <Select
            labelId="collection-select-label"
            id="collection-select"
            value={selectedCollection}
            onChange={(e) => setSelectedCollection(e.target.value)}
            label={t('sidebar.collection') || 'Collection'}
          >
            <MenuItem value="">
              <em>{t('sidebar.no_collection') || 'None'}</em>
            </MenuItem>
            {collections.map((collection) => (
              <MenuItem key={collection.id} value={collection.id}>
                {collection.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  };

  return (
    <CaseActionDialog
      open={open}
      onClose={handleClose}
      value={duplicateValue}
      onValueChange={setDuplicateValue}
      onSubmit={handleSubmit}
      type="copy"
      primaryColor={primaryColor}
      hoverColor={hoverColor}
      title={t('sidebar.copy_case') || 'Duplicate Case'}
      placeholder={t('sidebar.case_name') || 'Case Name'}
      submitButtonText={t('sidebar.copy_case') || 'Duplicate'}
      extraContent={renderExtraContent()}
    />
  );
};

export default CaseDuplicateDialog;
